import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/product/ProductCard";
import { ExternalLink, ShoppingCart, ShieldCheck, Truck, Undo2, ChevronsRight, Info } from "lucide-react";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const [compat, setCompat] = useState({ year: "", make: "", engine: "", part: "" });
  const [compatResult, setCompatResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/by-slug/${slug}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [slug]);

  const product = data?.product;
  const related = data?.related || [];

  const runCompat = () => {
    if (!product) return;
    const y = parseInt(compat.year);
    const match = (product.fitment || []).find(
      (f) => f.make?.toLowerCase() === compat.make.toLowerCase() &&
             (!y || (f.year_from <= y && f.year_to >= y))
    );
    if (match) {
      setCompatResult({ status: "confirmed", note: "This product is listed for the selected vehicle application. Review all engine, production-date, and part-number notes before purchasing." });
    } else if (compat.make || compat.year) {
      setCompatResult({ status: "unverified", note: "We could not confirm compatibility using the available product information. Contact our team for fitment assistance." });
    } else {
      setCompatResult(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-2 gap-8">
        <Skeleton className="aspect-square" />
        <div className="space-y-3"><Skeleton className="h-8 w-3/4" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-6 w-32" /></div>
      </div>
    );
  }
  if (!product) return <div className="max-w-3xl mx-auto p-16 text-center text-slate-500">Product not found.</div>;

  return (
    <div data-testid="product-page" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6 pb-4">
        <nav className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-[#0055FF]">Home</Link>
          <ChevronsRight className="w-3 h-3" />
          <Link to="/shop" className="hover:text-[#0055FF]">Shop</Link>
          {product.manufacturer && <><ChevronsRight className="w-3 h-3" /><Link to={`/shop?manufacturer=${encodeURIComponent(product.manufacturer)}`} className="hover:text-[#0055FF]">{product.manufacturer}</Link></>}
          <ChevronsRight className="w-3 h-3" />
          <span className="text-slate-900 truncate">{product.title.slice(0, 60)}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 pb-12 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="relative bg-slate-50 rounded-2xl overflow-hidden aspect-square border border-slate-200 group">
            <img
              src={product.images?.[activeImage] || product.main_image}
              alt={product.title}
              className="absolute inset-0 w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute top-4 left-4 flex flex-col gap-1">
              {product.condition && <Badge className="bg-[#0055FF] text-white uppercase font-mono tracking-widest text-[10px]">{product.condition}</Badge>}
              {product.has_test_report && <Badge className="bg-slate-900 text-white uppercase font-mono tracking-widest text-[10px]">Test Report</Badge>}
            </div>
          </div>
          {(product.images || []).length > 1 && (
            <div className="grid grid-cols-6 gap-2 mt-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImage(i)} className={`aspect-square rounded-md border overflow-hidden bg-slate-50 ${activeImage === i ? "border-[#0055FF]" : "border-slate-200"}`}>
                  <img src={img} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500">
            {product.manufacturer && <span>{product.manufacturer}</span>}
            {product.engine_size && <><span className="text-slate-300">·</span><span>{product.engine_size}</span></>}
            {product.region && <><span className="text-slate-300">·</span><span>{product.region}</span></>}
          </div>
          <h1 data-testid="product-title" className="mt-2 font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">{product.title}</h1>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div data-testid="product-price" className="text-3xl font-heading font-extrabold text-slate-900">{formatPrice(product.price)}</div>
            {product.stock > 0 ? (
              <span className="text-xs font-mono uppercase tracking-widest text-emerald-600">In stock · {product.stock} available</span>
            ) : (
              <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Out of stock</span>
            )}
          </div>
          {product.subtitle && <p className="mt-3 text-slate-600">{product.subtitle}</p>}

          {/* Part numbers */}
          {product.oem_part_numbers?.length > 0 && (
            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Part numbers</div>
              <div className="flex flex-wrap gap-1.5">
                {product.oem_part_numbers.map((pn) => (
                  <span key={pn} data-testid={`part-number-${pn}`} className="font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1">{pn}</span>
                ))}
                {product.sku && <span className="font-mono text-xs bg-white border border-slate-200 rounded px-2 py-1">SKU {product.sku}</span>}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="flex items-center border border-slate-200 rounded-md">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 hover:bg-slate-100">−</button>
              <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 h-11 text-center font-mono border-x border-slate-200" data-testid="product-qty-input" />
              <button onClick={() => setQty(qty + 1)} className="w-10 h-11 hover:bg-slate-100">+</button>
            </div>
            <Button data-testid="product-add-to-cart" onClick={() => add(product, qty)} className="h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC]">
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Add to cart
            </Button>
            {product.ebay_url && (
              <Button asChild data-testid="product-buy-ebay" variant="outline" className="h-11 px-6">
                <a href={product.ebay_url} target="_blank" rel="noopener noreferrer">
                  Buy on eBay <ExternalLink className="w-4 h-4 ml-1.5" />
                </a>
              </Button>
            )}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-2">
              <Truck className="w-4 h-4 text-[#0055FF] mt-0.5" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Shipping</div>
                <div className="text-xs text-slate-700">Nationwide USA</div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-2">
              <Undo2 className="w-4 h-4 text-[#0055FF] mt-0.5" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Returns</div>
                <div className="text-xs text-slate-700">30-day accepted</div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-[#0055FF] mt-0.5" />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Warranty</div>
                <div className="text-xs text-slate-700">{product.warranty || "See listing"}</div>
              </div>
            </div>
          </div>

          {/* Compatibility checker */}
          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="compat-checker">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <Info className="w-3 h-3" /> Compatibility checker
            </div>
            <div className="mt-2 font-heading font-semibold text-slate-900">Confirm this fits your vehicle</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Input placeholder="Year" value={compat.year} onChange={(e) => setCompat({ ...compat, year: e.target.value })} data-testid="compat-year" />
              <Input placeholder="Make" value={compat.make} onChange={(e) => setCompat({ ...compat, make: e.target.value })} data-testid="compat-make" />
              <Input placeholder="Engine (opt)" value={compat.engine} onChange={(e) => setCompat({ ...compat, engine: e.target.value })} data-testid="compat-engine" />
              <Button onClick={runCompat} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="compat-check">Check</Button>
            </div>
            {compatResult && (
              <div data-testid="compat-result" className={`mt-3 rounded-md p-3 text-sm border ${compatResult.status === "confirmed" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                {compatResult.note}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
          <Tabs defaultValue="overview">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="compatibility" data-testid="tab-compatibility">Vehicle compatibility</TabsTrigger>
              <TabsTrigger value="numbers" data-testid="tab-numbers">Part numbers</TabsTrigger>
              <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping & returns</TabsTrigger>
              {product.testing_info && <TabsTrigger value="testing">Testing</TabsTrigger>}
              {product.ebay_url && <TabsTrigger value="ebay">eBay listing</TabsTrigger>}
            </TabsList>
            <TabsContent value="overview" className="mt-4 rounded-xl bg-white border border-slate-200 p-6 text-slate-700 leading-relaxed">
              {product.description}
            </TabsContent>
            <TabsContent value="compatibility" className="mt-4 rounded-xl bg-white border border-slate-200 p-6">
              {product.fitment?.length ? (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="py-2">Year Range</th><th className="py-2">Make</th><th className="py-2">Model</th><th className="py-2">Notes</th></tr></thead>
                  <tbody>
                    {product.fitment.map((f, i) => (
                      <tr key={i} className="border-b border-slate-100"><td className="py-2 font-mono">{f.year_from}–{f.year_to}</td><td>{f.make}</td><td>{f.model || "—"}</td><td className="text-slate-500">{f.notes || "—"}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="text-sm text-slate-500">See listing for compatibility details.</div>}
            </TabsContent>
            <TabsContent value="numbers" className="mt-4 rounded-xl bg-white border border-slate-200 p-6">
              <div className="space-y-2 text-sm">
                <div><span className="text-slate-500 font-mono text-xs uppercase tracking-widest">OEM: </span><span className="font-mono">{product.oem_part_numbers?.join(", ") || "—"}</span></div>
                <div><span className="text-slate-500 font-mono text-xs uppercase tracking-widest">Manufacturer: </span><span className="font-mono">{product.mfr_part_numbers?.join(", ") || "—"}</span></div>
                <div><span className="text-slate-500 font-mono text-xs uppercase tracking-widest">Interchange: </span><span className="font-mono">{product.interchange_numbers?.join(", ") || "—"}</span></div>
                <div><span className="text-slate-500 font-mono text-xs uppercase tracking-widest">SKU: </span><span className="font-mono">{product.sku || "—"}</span></div>
              </div>
            </TabsContent>
            <TabsContent value="shipping" className="mt-4 rounded-xl bg-white border border-slate-200 p-6 text-sm text-slate-700 space-y-3">
              <div><b>Shipping:</b> {product.shipping_info}</div>
              <div><b>Returns:</b> {product.return_info}</div>
              {product.warranty && <div><b>Warranty:</b> {product.warranty}</div>}
            </TabsContent>
            {product.testing_info && (
              <TabsContent value="testing" className="mt-4 rounded-xl bg-white border border-slate-200 p-6 text-sm text-slate-700">
                {product.testing_info}
              </TabsContent>
            )}
            {product.ebay_url && (
              <TabsContent value="ebay" className="mt-4 rounded-xl bg-white border border-slate-200 p-6 text-sm text-slate-700">
                <div>eBay item number: <span className="font-mono">{product.ebay_item_number}</span></div>
                <div className="mt-1">Last synchronized: <span className="font-mono">{product.ebay_last_synced ? new Date(product.ebay_last_synced).toLocaleString() : "—"}</span></div>
                <a href={product.ebay_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-[#0055FF] hover:underline">
                  View listing on eBay <ExternalLink className="w-3 h-3" />
                </a>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>

      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mb-4">Related products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
